import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseQueryOptions, useQuery } from 'react-query'
import { Batch } from 'types'
import { useEffect, useMemo, useRef } from 'react'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  fetched_posts: number
  analyzed_posts: number
  total_posts: number
  brain_channel_insights_status: Batch
  posts_fetching_status: Batch
  has_fetched_posts: boolean
}

export const fetch = async ({
  workspaceId,
  integrationId,
  signal,
}: {
  workspaceId: number
  integrationId: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/external_integrations/${integrationId}/insights_progress`, {
    signal,
  })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useIntegrationInsightsProgress = (
  integrationId?: number,
  options?: UseQueryOptions<Data> & {
    /**
     * Set to `true` to poll every 500 ms until the batch is complete.
     * Default is `false` (no polling).
     */
    pollUntilFinal?: boolean
    /**
     * Callback that fires when the batch is completed.
     * Only called once when the batch transitions from incomplete to complete.
     */
    onComplete?: () => void
  },
) => {
  const workspaceId = useCurrentWorkspaceId()
  const hasCompletedRef = useRef(false)

  const { pollUntilFinal = false, onComplete, ...queryOptions } = options ?? {}

  const pollingInterval = (data: Data | undefined): number | false => {
    if (!pollUntilFinal) return false
    if (!data) return 500

    // Check if both batches are complete
    const isComplete =
      !!data.has_fetched_posts &&
      data.analyzed_posts === data.total_posts &&
      !data.brain_channel_insights_status &&
      !data.posts_fetching_status
    return isComplete ? false : 500
  }

  const query = useQuery<Data>(
    ['workspaces', workspaceId, 'externalIntegrations', integrationId, 'insightsProgress'],
    () => {
      if (!integrationId) throw new Error('No integrationId provided')
      return fetch({ workspaceId, integrationId })
    },
    {
      refetchInterval: queryOptions.refetchInterval ?? pollingInterval,
      refetchOnWindowFocus: false,
      staleTime: (queryOptions.cacheTime || 0) * 0.5,
      enabled: !!integrationId,
      ...queryOptions,
    },
  )

  const hasFinished = useMemo(() => {
    const allPostsAnalyzed =
      !!query.data?.has_fetched_posts &&
      !!query.data?.analyzed_posts &&
      query.data?.analyzed_posts > 0 &&
      query.data?.analyzed_posts === query.data?.total_posts

    return allPostsAnalyzed && !query.data?.brain_channel_insights_status && !query.data?.posts_fetching_status
  }, [query.data])

  // Handle the onComplete callback
  useEffect(() => {
    if (!!hasFinished && !hasCompletedRef.current && onComplete) {
      hasCompletedRef.current = true
      onComplete()
    }
  }, [onComplete, hasFinished])

  // Reset the completion flag when integrationId changes
  useEffect(() => {
    hasCompletedRef.current = false
  }, [integrationId])

  return query
}
